import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";
import "@/App.css";
import "./styles/readable-copy.css";

import Layout from "./components/Layout";
import Home from "./pages/Home";
import { captureUtmFromUrl } from "./lib/analytics";

// Lazy-loaded routes — split into separate chunks, load on-demand.
// This cuts the initial JS bundle dramatically and improves first paint.

const Insights = lazy(() => import("./pages/InsightsHubV2"));
const InsightArticle = lazy(() => import("./pages/InsightArticleV2"));
const Energetics = lazy(() => import("./pages/Energetics"));
const EnergeticsCategory = lazy(() => import("./pages/EnergeticsCategory"));
const CartridgeDetail = lazy(() => import("./pages/CartridgeDetail"));
const CustomCartridgeManufacturing = lazy(() =>
  import("./pages/CustomCartridgeManufacturing")
);
const MountsMechanical = lazy(() => import("./pages/MountsMechanical"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Capabilities = lazy(() => import("./pages/Capabilities"));
const AerospaceMachining = lazy(() => import("./pages/AerospaceMachining"));
const QualityTesting = lazy(() => import("./pages/QualityTesting"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const Sustainability = lazy(() => import("./pages/Sustainability"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const NotFound = lazy(() => import("./pages/NotFound"));

// AnalyticsLoader is non-critical for first paint. Lazy-loading it lets
// webpack pull the code out of the main bundle (–a few KB parsed on the
// critical path) and defers side-effects until after the hero has painted.
// Wrapping in <Suspense> is enough — it renders nothing visible.
const AnalyticsLoader = lazy(() => import(/* webpackPrefetch: true */ "./components/AnalyticsLoader"));
const ConsentBanner = lazy(() => import("./components/ConsentBanner"));

// Minimal in-flight fallback — matches the navy aesthetic, no flash.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-ivory flex items-center justify-center">
      <div className="font-mono text-[12.5px] tracking-[0.32em] text-navy/85 uppercase flex items-center gap-3">
        <span className="w-1.5 h-1.5 bg-signal animate-pulse" />
        Loading
      </div>
    </div>
  );
}

// Slug-preserving 301: /mounts-mechanical/:slug → /mounts/:slug so any
// internal links still using the pre-migration URL land on the correct
// product detail page instead of the products index.
function LegacyMountRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/mounts/${slug}`} replace />;
}

function App() {
  useEffect(() => {
    // Prevent Emergent-hosted preview/staging domains from being indexed
    // by search engines. Only the canonical www.valortactical.us should
    // appear in Google — everything else stays private.
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const canonicalHosts = ["www.valortactical.us", "valortactical.us"];
      if (host && !canonicalHosts.includes(host)) {
        const existing = document.querySelector('meta[name="robots"]');
        if (existing) existing.setAttribute("content", "noindex, nofollow");
        else {
          const m = document.createElement("meta");
          m.name = "robots";
          m.content = "noindex, nofollow";
          document.head.appendChild(m);
        }
      }
    }

    // Capture UTMs on first load (sessionStorage, first-touch).
    captureUtmFromUrl();

    // Block context menu (right-click / long-press) on all <img> and
    // <video> elements site-wide. Casual save-as prevention only —
    // determined users can still pull assets from DevTools/Network,
    // but this stops the 95% case (right-click → "Save image as").
    const onContextMenu = (e) => {
      const tag = e.target && e.target.tagName;
      if (tag === "IMG" || tag === "VIDEO" || tag === "SOURCE") {
        e.preventDefault();
      }
    };
    document.addEventListener("contextmenu", onContextMenu);

    // Smooth-scroll (Lenis) intentionally disabled — dragging the native
    // scrollbar with a mouse fights Lenis's own scroll animation and
    // causes visible jitter. Modern browsers already provide smooth-enough
    // wheel scrolling, and Framer Motion parallax works on native
    // scroll events without any bridge.
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
    };
  }, []);

  return (
    <HelmetProvider>
      <LazyMotion features={domAnimation} strict>
        <Suspense fallback={null}>
          <AnalyticsLoader />
        </Suspense>
        <BrowserRouter>
          <Suspense fallback={null}>
            <ConsentBanner />
          </Suspense>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<Layout />}>
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<InsightArticle />} />
            </Route>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/energetics" element={<Energetics />} />
              {/* Custom Cartridge Manufacturing — service page (must sit above the
                  dynamic /:categorySlug route so React Router matches the
                  literal path first). */}
              <Route
                path="/energetics/custom-cartridge-manufacturing"
                element={<CustomCartridgeManufacturing />}
              />
              {/* Legacy slug: the BBU-36/B page used to live at /bbu-35 */}
              <Route path="/energetics/cartridges/bbu-35" element={<Navigate to="/energetics/cartridges/bbu-36-b" replace />} />
              {/* Individual cartridge product-detail pages — /energetics/cartridges/:slug */}
              <Route
                path="/energetics/cartridges/:slug"
                element={<CartridgeDetail />}
              />
              {/* Six category / application pages under the Energetics hub. */}
              <Route
                path="/energetics/:categorySlug"
                element={<EnergeticsCategory />}
              />
              {/* Products index — /mounts-mechanical is the canonical URL for the
                  reorganized products page; the old /weapon-systems-enhancements
                  and other legacy paths 301 to it below. */}
              <Route path="/mounts-mechanical" element={<MountsMechanical />} />
              {/* Product detail pages — the URL /mounts/:slug is preserved
                  1:1 from the Webflow site so Google-indexed URLs stay live. */}
              <Route path="/mounts/:slug" element={<ProductDetail />} />
              <Route path="/capabilities" element={<Capabilities />} />
              <Route path="/capabilities/aerospace-machining" element={<AerospaceMachining />} />
              <Route path="/quality-testing" element={<QualityTesting />} />
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/careers/jobs/:jobId" element={<JobDetail />} />
              <Route path="/careers/jobs/:jobId/:slug" element={<JobDetail />} />
              <Route path="/sustainability" element={<Sustainability />} />
              {/* Contact — /contact-us is preserved 1:1 from the Webflow site. */}
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Legacy Webflow URLs — client-side 301 fallback.
                  Server-level 301s at Cloudflare take precedence in
                  production; these guarantee visitors + crawlers never
                  land on a 404 for a known legacy path. */}
              <Route path="/old-home-2" element={<Navigate to="/" replace />} />
              {/* /contact was the new-site's earlier route; keep the 301 so
                  outbound links from analytics tools, drafts, or manual URLs
                  still resolve to the canonical /contact-us. */}
              <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
              <Route path="/weapon-systems-enhancements" element={<Navigate to="/mounts-mechanical" replace />} />
              <Route path="/m205-tripod-mount" element={<Navigate to="/mounts/m205" replace />} />
              {/* /mounts-mechanical/:slug was the new-site's earlier URL for
                  product detail; 301 to the preserved /mounts/:slug. */}
              <Route path="/mounts-mechanical/:slug" element={<LegacyMountRedirect />} />
              <Route path="/mounts/atem" element={<Navigate to="/mounts-mechanical" replace />} />
              <Route path="/individual-barrels/*" element={<Navigate to="/mounts-mechanical" replace />} />
              <Route path="/barrels/*" element={<Navigate to="/mounts-mechanical" replace />} />
              {/* Real 404 — anything not matched above renders NotFound with
                  noindex, no redirect. Preserves the URL in the browser so
                  users can see what they typed and share the broken link. */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
        </BrowserRouter>
      </LazyMotion>
    </HelmetProvider>
  );
}

export default App;
